import { useEffect, useState } from "react";
import { getMaxNutriValues, MealSourceDto } from "../api";
import { emptyNutriValue, NutriValues } from "../data/nutriValues";

export type NutriSliderContext = "food" | "purchase" | "dish";
export const useNutriSlider = (
  context: NutriSliderContext,
): NutriSliderProps => {
  const [minFat, setMinFat] = useState(0);
  const [maxFat, setMaxFat] = useState<number | undefined>(undefined);
  const [minCarbs, setMinCarbs] = useState(0);
  const [maxCarbs, setMaxCarbs] = useState<number | undefined>(undefined);
  const [minProtein, setMinProtein] = useState(0);
  const [maxProtein, setMaxProtein] = useState<number | undefined>(undefined);
  const [minKcal, setMinKcal] = useState(0);
  const [maxKcal, setMaxKcal] = useState<number | undefined>(undefined);
  return {
    context,
    minFat,
    setMinFat,
    maxFat,
    setMaxFat,
    minCarbs,
    setMinCarbs,
    maxCarbs,
    setMaxCarbs,
    minProtein,
    setMinProtein,
    maxProtein,
    setMaxProtein,
    minKcal,
    setMinKcal,
    maxKcal,
    setMaxKcal,
  };
};
/**
 * Lädt die maximalen Nährwerte passend zu den gewählten Typen. `getMaxNutriValues`
 * kennt nur die Contexts `dish` (benötigt `personId`), `food` und `purchase`; für
 * mehrere Typen (oder „Alle", also keine Auswahl) wird elementweise das Maximum
 * aus allen relevanten Contexts gebildet.
 */
export function useMaxNutriValues(
  indicators: MealSourceDto["indicator"][],
  personId: number | undefined,
  enabled = true,
): NutriValues | undefined {
  const [maxValues, setMaxValues] = useState<NutriValues | undefined>();
  const indicatorKey = [...indicators].sort().join(",");

  useEffect(() => {
    if (!enabled) return;
    let cancelled = false;
    const contexts: NutriSliderContext[] = (() => {
      if (indicators.length === 0) {
        return personId != null ? ["purchase", "dish"] : ["purchase"];
      }
      const selected = new Set<NutriSliderContext>();
      if (indicators.includes("DISH")) {
        selected.add(personId != null ? "dish" : "purchase");
      }
      if (indicators.includes("FOOD")) selected.add("food");
      if (indicators.includes("PURCHASE")) selected.add("purchase");
      return [...selected];
    })();

    Promise.all(
      contexts.map((context) =>
        getMaxNutriValues({
          path: { context },
          query: { personId: personId != null ? String(personId) : undefined },
        }).then((response) => response.data),
      ),
    )
      .then((values) => {
        if (cancelled) return;
        setMaxValues(
          values.reduce<NutriValues>(
            (acc, value) => ({
              kcal: Math.max(acc.kcal, value.kcal),
              fat: Math.max(acc.fat, value.fat),
              carbs: Math.max(acc.carbs, value.carbs),
              protein: Math.max(acc.protein, value.protein),
            }),
            emptyNutriValue,
          ),
        );
      })
      .catch(() => {
        if (!cancelled) setMaxValues(undefined);
      });

    return () => {
      cancelled = true;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [indicatorKey, personId, enabled]);

  return maxValues;
}

export type NutriSliderProps = {
  context: NutriSliderContext;
  personId?: number;
  minFat: number;
  setMinFat: (v: number) => void;
  maxFat: number | undefined;
  setMaxFat: (v: number | undefined) => void;
  minCarbs: number;
  setMinCarbs: (v: number) => void;
  maxCarbs: number | undefined;
  setMaxCarbs: (v: number | undefined) => void;
  minProtein: number;
  setMinProtein: (v: number) => void;
  maxProtein: number | undefined;
  setMaxProtein: (v: number | undefined) => void;
  minKcal: number;
  setMinKcal: (v: number) => void;
  maxKcal: number | undefined;
  setMaxKcal: (v: number | undefined) => void;
};
